import { Dispatch, SetStateAction, useCallback } from 'react'
import { createGroupFromSelection, ungroupSelection } from '../../elements/groups'
import {
    bringElementsForward,
    bringElementsToFront,
    rebuildElementsByTopOrder,
    reorderGroupChildren,
    sendElementsBackward,
    sendElementsToBack,
} from '../../elements/zOrder'
import { TemplateElement } from '../../templateEditor.types'
import { useTemplateEditorHistory } from './useTemplateEditorHistory'

type HistoryApi = ReturnType<typeof useTemplateEditorHistory>

type Params = {
    elements: TemplateElement[]
    selectedIds: string[]
    setElements: Dispatch<SetStateAction<TemplateElement[]>>
    history: HistoryApi
}

export const useTemplateEditorLayers = ({ elements, selectedIds, setElements, history }: Params) => {
    const withTargets = useCallback(
        (ids: string[] | undefined, apply: (targetIds: string[]) => void) => {
            const targetIds = ids && ids.length > 0 ? ids : selectedIds
            if (targetIds.length === 0) {
                return
            }
            history.pushHistory()
            apply(targetIds)
        },
        [history, selectedIds]
    )

    const groupSelection = useCallback(() => {
        const result = createGroupFromSelection(elements, selectedIds)
        if (!result) {
            return false
        }
        history.mutateElements(() => result.elements, [result.groupId])
        return true
    }, [elements, history, selectedIds])

    const ungroupSelectionAction = useCallback(() => {
        const result = ungroupSelection(elements, selectedIds)
        if (!result) {
            return false
        }
        history.mutateElements(() => result.elements, result.childIds)
        return true
    }, [elements, history, selectedIds])

    const toggleLock = useCallback(
        (ids?: string[]) => {
            withTargets(ids, (targetIds) => {
                const idSet = new Set(targetIds)
                setElements((prev) =>
                    prev.map((element) => (idSet.has(element.id) ? { ...element, locked: !element.locked } : element))
                )
            })
        },
        [setElements, withTargets]
    )

    const toggleHidden = useCallback(
        (ids?: string[]) => {
            withTargets(ids, (targetIds) => {
                const idSet = new Set(targetIds)
                setElements((prev) =>
                    prev.map((element) => (idSet.has(element.id) ? { ...element, hidden: !element.hidden } : element))
                )
            })
        },
        [setElements, withTargets]
    )

    const reorderLayers = useCallback(
        (fromVisualIndex: number, toVisualIndex: number) => {
            if (fromVisualIndex === toVisualIndex) {
                return
            }

            history.pushHistory()
            setElements((prev) => {
                const topLevel = prev.filter((element) => !element.groupId)
                const visual = [...topLevel].reverse()
                if (
                    fromVisualIndex < 0 ||
                    toVisualIndex < 0 ||
                    fromVisualIndex >= visual.length ||
                    toVisualIndex >= visual.length
                ) {
                    return prev
                }

                const nextVisual = [...visual]
                const [moved] = nextVisual.splice(fromVisualIndex, 1)
                nextVisual.splice(toVisualIndex, 0, moved)
                return rebuildElementsByTopOrder(prev, [...nextVisual].reverse())
            })
        },
        [history, setElements]
    )

    const reorderGroupLayers = useCallback(
        (groupId: string, fromVisualIndex: number, toVisualIndex: number) => {
            if (fromVisualIndex === toVisualIndex) {
                return
            }

            history.pushHistory()
            setElements((prev) => {
                const group = prev.find((element) => element.id === groupId && element.type === 'group')
                if (!group || group.type !== 'group') {
                    return prev
                }
                const count = group.children.length
                if (fromVisualIndex < 0 || toVisualIndex < 0 || fromVisualIndex >= count || toVisualIndex >= count) {
                    return prev
                }
                return reorderGroupChildren(prev, groupId, count - 1 - fromVisualIndex, count - 1 - toVisualIndex)
            })
        },
        [history, setElements]
    )

    const bringToFront = useCallback(
        (ids?: string[]) => {
            withTargets(ids, (targetIds) => {
                setElements((prev) => bringElementsToFront(prev, targetIds))
            })
        },
        [setElements, withTargets]
    )

    const sendToBack = useCallback(
        (ids?: string[]) => {
            withTargets(ids, (targetIds) => {
                setElements((prev) => sendElementsToBack(prev, targetIds))
            })
        },
        [setElements, withTargets]
    )

    const bringForward = useCallback(
        (ids?: string[]) => {
            withTargets(ids, (targetIds) => {
                setElements((prev) => bringElementsForward(prev, targetIds))
            })
        },
        [setElements, withTargets]
    )

    const sendBackward = useCallback(
        (ids?: string[]) => {
            withTargets(ids, (targetIds) => {
                setElements((prev) => sendElementsBackward(prev, targetIds))
            })
        },
        [setElements, withTargets]
    )

    return {
        groupSelection,
        ungroupSelection: ungroupSelectionAction,
        toggleLock,
        toggleHidden,
        reorderLayers,
        reorderGroupLayers,
        bringToFront,
        sendToBack,
        bringForward,
        sendBackward,
    }
}
